import { addMonths, format } from "date-fns";
import { NotificationManager } from "react-notifications";
import { uuid } from "uuidv4";
import { Actions as COMPANY_ACTIONS } from "../../../../store/actions/companyActions";
import { Actions as EMPLOYEEACTIONS } from "../../../../store/actions/employeeActions";
import { Actions as INTEGRATION_ACTIONS } from "../../../../store/actions/integrationActions";
import { Actions as NOTIFICATION_ACTIONS } from "../../../../store/actions/notificationActions";

import { createRequest } from "@/components/common/helpers/RequestHelpers";
import { cloneDeep } from "lodash";

const bambooRequest = createRequest({
	urlPath: "/integrations/bamboo",
	// local: true,
});

const integrationRequest = createRequest({
	urlPath: "/integrations",
	// local: true,
});

const Types = {
	BAMBOO_SETTINGS: "BAMBOO_SETTINGS",
	BAMBOO_IMPORT_LEAVE: "BAMBOO_IMPORT_LEAVE",
	BAMBOO_EMPLOYEE_LINKS: "BAMBOO_EMPLOYEE_LINKS",
	BAMBOO_GETTING_EMPLOYEES: "BAMBOO_GETTING_EMPLOYEES",
	BAMBOO_RESYNC_EMPLOYEES: "BAMBOO_RESYNC_EMPLOYEES",
	BAMBOO_RESYNC_EMPLOYEE: "BAMBOO_RESYNC_EMPLOYEE",
	BAMBOO_IMPORT_DEPARTMENTS: "BAMBOO_IMPORT_DEPARTMENTS",
	BAMBOO_SETTINGS_SAVING: "BAMBOO_SETTINGS_SAVING",
	BAMBOO_UPDATE_MODAL: "BAMBOO_UPDATE_MODAL",
	BAMBOO_LOADING_PUSH_CHANGES: "BAMBOO_LOADING_PUSH_CHANGES",
	BAMBOO_SAVING_PUSH_CHANGES: "BAMBOO_SAVING_PUSH_CHANGES",
};

// const setImportingTimesheets = (status) => ({
// 	type: Types.BAMBOO_IMPORT_TIMESHEETS,
// 	status,
// });
const toggleUpdateModal = ({ data, status }) => ({
	type: Types.BAMBOO_UPDATE_MODAL,
	data,
	status,
});

const setImportingLeave = (status) => ({
	type: Types.BAMBOO_IMPORT_LEAVE,
	status,
});
const setLoadingPushChanges = (status) => ({
	type: Types.BAMBOO_LOADING_PUSH_CHANGES,
	status,
});
const setSavingPushChanges = (status) => ({
	type: Types.BAMBOO_SAVING_PUSH_CHANGES,
	status,
});
const setResyncingEmployees = (status) => ({
	type: Types.BAMBOO_RESYNC_EMPLOYEES,
	status,
});
const setResyncingEmployee = (status) => ({
	type: Types.BAMBOO_RESYNC_EMPLOYEE,
	status,
});
const setSettings = (settings) => ({
	type: Types.BAMBOO_SETTINGS,
	settings,
});
const gettingEmployees = (status) => ({
	type: Types.BAMBOO_GETTING_EMPLOYEES,
	status,
});
const bambooSettingsSaving = (status) => ({
	type: Types.BAMBOO_SETTINGS_SAVING,
	status,
});
const updateEmployeeLinks = ({ links, employees, ignoredEmployees }) => ({
	type: Types.BAMBOO_EMPLOYEE_LINKS,
	links,
	employees,
	ignoredEmployees,
});

const connectBamboo = (credentials) => {
	return async (dispatch, getState) => {
		const url = window.location.href;

		await bambooRequest({
			method: "GET",
			path: `/connect`,
			query: { url, ...credentials },
		})
			.then(() => {
				dispatch(INTEGRATION_ACTIONS.integrationGetList());
				NotificationManager.success("Bamboo HR Connected Successfully!", "Connecting Bamboo HR");
			})
			.catch((error) => {
				console.error(error);
				let message = error.response?.data?.error;
				NotificationManager.error(message ? message : "Error Connecting Bamboo HR", "Connecting Bamboo HR");
			});
	};
};

const disconnectBamboo = () => {
	return async (dispatch, getState) => {
		await bambooRequest({
			method: "POST",
			path: `/revoke`,
		})
			.then(() => {
				dispatch(INTEGRATION_ACTIONS.integrationGetList());
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error("Error Disconnecting Bamboo HR", "Disconnect Bamboo HR");
			});
	};
};

const importLeave = (start, end) => {
	return async (dispatch, getState) => {
		dispatch(setImportingLeave(true));
		NotificationManager.info("Importing Leave from Bamboo HR", "Importing From Bamboo HR");

		await bambooRequest({
			path: `/resync/leave`,
			query: { start, end },
			method: "GET",
		})
			.then(({ data }) => {
				dispatch(setImportingLeave(false));
				NotificationManager.success("Successfully Imported Leave from Bamboo!", "Importing From Bamboo HR");
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error("Error Importing Leave from Bamboo HR", "Importing From Bamboo HR");
				dispatch(setImportingLeave(false));
			});
	};
};

const linkEmployee = ({ agreement, referenceLink }) => {
	return async (dispatch, getState) => {
		await integrationRequest({
			path: `/Bamboo HR/link-employee`,
			method: "POST",
			body: {
				agreementId: agreement,
				link: referenceLink,
			},
		})
			.then(({ data }) => {
				dispatch(getBambooEmployees());
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error("Error Linking Employee with Bamboo HR", "Linking Employee with Bamboo HR");
			});
	};
};

const ignoreEmployee = (references) => {
	return async (dispatch, getState) => {
		if (!Array.isArray(references)) return;
		try {
			await Promise.all(
				references?.map((referenceLink) => {
					return new Promise(async (resolve) => {
						await bambooRequest({
							method: "POST",
							path: `/employee`,
							body: {
								agreement: "ignored",
								referenceLink,
							},
						}).then(({ data }) => resolve(data));
					});
				}),
			);
			NotificationManager.success(
				`Added ${references?.length || 0} Employee${references?.length > 1 ? "s" : ""} To Ignore List`,
				`Ignoring Employee${references?.length > 1 ? "s" : ""}`,
			);
		} catch (error) {
			console.error(error);
			NotificationManager.error(
				`Error Adding Employee${references?.length > 1 ? "s" : ""} To Ignore List`,
				`Ignoring Employee${references?.length > 1 ? "s" : ""}`,
			);
		}
		dispatch(getBambooEmployees());
	};
};

const importEmployees = (references) => {
	return async (dispatch, getState) => {
		if (!Array.isArray(references)) return;
		await bambooRequest({
			method: "POST",
			path: `/import/employees`,
			body: {
				employeeList: references,
			},
		})
			.then(({ data }) => {
				dispatch(getBambooEmployees());
				NotificationManager.success(
					`Imported ${references?.length || 0} Employee${references?.length > 1 ? "s" : ""}`,
					`Importing Employee${references?.length > 1 ? "s" : ""}`,
				);
				dispatch(INTEGRATION_ACTIONS.toggleImportModal());
				dispatch(
					EMPLOYEEACTIONS.employeesGetListWithSalary(
						{
							from: format(addMonths(new Date(), -2), "yyyy-MM-01"), // get list from last 2 months
							to: format(new Date(), "yyyy-12-31"),
						},
						{ includeTax: true },
					),
				);
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error(
					`Error Importing Employee${references?.length > 1 ? "s" : ""} To Ignore List`,
					`Importing Employee${references?.length > 1 ? "s" : ""}`,
				);
			});
	};
};

const getBambooEmployees = () => {
	return async (dispatch, getState) => {
		const state = getState();
		if (!state.integration?.hris) return;
		if (state.integration?.hris?.bambooHr?.loadingEmployees) return;
		dispatch(gettingEmployees(true));
		await bambooRequest({ path: `/employees`, method: "GET" })
			.then(({ data }) => {
				dispatch(updateEmployeeLinks(data));
			})
			.catch((error) => {
				console.error(error);
				dispatch(gettingEmployees());
			});
	};
};

const getBambooSettings = () => {
	return async (dispatch, getState) => {
		await bambooRequest({ path: `/settings`, method: "GET" })
			.then(({ data }) => dispatch(setSettings(data)))
			.catch((error) => {
				console.error(error);
				NotificationManager.error("Error Getting Bamboo HR Settings", "Bamboo HR Settings");
			});
	};
};

const resyncEmployees = () => {
	return async (dispatch, getState) => {
		dispatch(setResyncingEmployees(true));
		await bambooRequest({
			path: `/resync/employees`,
			method: "GET",
		})
			.then(({ data }) => {
				NotificationManager.success("Successfully resynced Employees!", "Bamboo HR Employees");
				dispatch(NOTIFICATION_ACTIONS.getNotifications());
				dispatch(getBambooSettings());
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error("Error Resyncing Bamboo HR Employees", "Bamboo HR Employees");
			});
		dispatch(setResyncingEmployees());
	};
};

const pushChanges = ({ agreement, section }) => {
	return async (dispatch, getState) => {
		dispatch(setLoadingPushChanges(true));
		dispatch(toggleUpdateModal({ status: true }));
		await bambooRequest({ path: `/update/${agreement}/${section}`, method: "GET" })
			.then(({ data }) => {
				dispatch(toggleUpdateModal({ data: { ...data, agreement, section }, status: true }));
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error("Error Updating Bamboo HR Employee", "Bamboo HR Employee");
			});
		dispatch(setLoadingPushChanges());
	};
};

const confirmPushChanges = () => {
	return async (dispatch, getState) => {
		const state = getState();
		const { agreement, section, data } = { ...state.integration?.hris?.bambooHr?.pushChangesModal };

		dispatch(setSavingPushChanges(true));
		await bambooRequest({
			path: `/update/${agreement}/${section}`,
			method: "POST",
			body: data,
		})
			.then(({ data }) => {
				dispatch(toggleUpdateModal({}));
				NotificationManager.success("Successfully Updated Bamboo HR Employee", "Bamboo HR Employee");
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error("Error Updating Bamboo HR Employee", "Bamboo HR Employee");
			});
		dispatch(setSavingPushChanges());
	};
};

const resyncEmployee = (agreement) => {
	return async (dispatch, getState) => {
		dispatch(setResyncingEmployee(true));
		await bambooRequest({ path: `/resync/employee`, query: { agreement }, method: "GET" })
			.then(({ data }) => {
				NotificationManager.success("Successfully resynced Employee!", "Bamboo HR Employee");
				dispatch(NOTIFICATION_ACTIONS.getNotifications());
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error("Error Resyncing Bamboo HR Employee", "Bamboo HR Employee");
			});
		dispatch(setResyncingEmployee());
	};
};

const saveBambooSettings = (body, preventNotification) => {
	return async (dispatch, getState) => {
		dispatch(bambooSettingsSaving(true));
		await integrationRequest({ path: `/Bamboo HR/update-settings`, method: "POST", body })
			.then(({ data }) => {
				dispatch(INTEGRATION_ACTIONS.integrationGetList());
				dispatch(getBambooSettings());
				if (preventNotification) return;
				NotificationManager.success("Successfully Saved Bamboo HR Settings", "Bamboo HR Settings");
			})
			.catch((error) => {
				console.error(error);
				NotificationManager.error("Error Saving Bamboo HR Settings", "Bamboo HR Settings");
			});
		dispatch(bambooSettingsSaving());
	};
};

const importDepartmentsModal = (status) => ({
	type: Types.BAMBOO_IMPORT_DEPARTMENTS,
	status,
});

const saveDepartments = (departmentsToAdd, mapFields) => {
	return async (dispatch, getState) => {
		const state = getState();

		const { departments, org } = { ...state.company?.settings };

		const departmentArray = [...(Array.isArray(departments) ? departments : [])];

		departmentsToAdd.map((department) => {
			departmentArray.push({
				id: uuid(),
				name: department,
				bambooReference: department,
			});
		});

		NotificationManager.warn("Importing Departments", "Importing Bamboo Departments");

		try {
			await dispatch(COMPANY_ACTIONS.updateCompanyDetails({ departments: departmentArray, org }, true));

			if (mapFields) {
				const { mappedFields, mappedOptions: toMap } = { ...state.integration?.hris?.bambooHr?.settings };

				const mappedOptions = toMap instanceof Object ? cloneDeep(toMap) : {};

				departmentArray.map((department) => {
					mappedOptions["department"] = {
						...mappedOptions["department"],
						[department.bambooReference]: department.id,
					};
				});

				await dispatch(
					saveBambooSettings(
						{
							mappedFields,
							mappedOptions,
						},
						true,
					),
				);
			}
			NotificationManager.success("Departments Imported Successfully!", "Importing Bamboo Departments");
			dispatch(importDepartmentsModal());
		} catch (error) {
			NotificationManager.error("There was an error importing departments", "Importing Bamboo Departments");
			console.error(error);
		}
	};
};

const Actions = {
	connectBamboo,
	disconnectBamboo,
	importLeave,
	linkEmployee,
	getBambooSettings,
	saveBambooSettings,
	getBambooEmployees,
	resyncEmployees,
	ignoreEmployee,
	importDepartmentsModal,
	importEmployees,
	saveDepartments,
	resyncEmployee,
	pushChanges,
	toggleUpdateModal,
	confirmPushChanges,
};

export { Actions, Types };
